/**
 * noOrphans — JS fallback for browsers that don't yet support
 * CSS `text-wrap: pretty`.
 *
 * Walks every paragraph-ish element and replaces the LAST whitespace
 * with a non-breaking space (\u00A0), which prevents the final word
 * from becoming a single-word orphan on the last line.
 *
 * Idempotent: marks processed elements with `data-orphan-fixed` so
 * we never double-glue on re-runs (e.g. after a React route change).
 *
 * Performance: runs only when CSS.supports reports `text-wrap: pretty`
 * is unsupported, so modern browsers skip this entirely.
 */
const SELECTOR = "p, li, dd, blockquote, figcaption, h1, h2, h3, h4, h5, h6";

export function applyNoOrphans(root = document) {
  // Fast path: modern browsers handle this natively.
  if (typeof CSS !== "undefined" && CSS.supports && CSS.supports("text-wrap", "pretty")) {
    return;
  }

  const nodes = root.querySelectorAll(SELECTOR);
  nodes.forEach((el) => {
    if (el.dataset.orphanFixed === "1") return;

    // Only touch elements whose direct child is a single text-node-bearing run.
    // Skip elements that contain block-level children (we shouldn't rewrite their structure).
    const hasBlockChild = Array.from(el.children).some((c) => {
      const display = window.getComputedStyle(c).display;
      return display === "block" || display === "flex" || display === "grid";
    });
    if (hasBlockChild) return;

    const text = el.textContent || "";
    const trimmed = text.replace(/\s+$/, "");
    const lastSpace = trimmed.lastIndexOf(" ");
    if (lastSpace <= 0) return;

    // Walk the DOM and replace the LAST regular space we encounter with \u00A0.
    // We process from the last text node backward to find the actual whitespace.
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let n;
    while ((n = walker.nextNode())) textNodes.push(n);

    for (let i = textNodes.length - 1; i >= 0; i--) {
      const node = textNodes[i];
      const idx = node.nodeValue.replace(/\s+$/, "").lastIndexOf(" ");
      if (idx > 0) {
        const v = node.nodeValue;
        const trail = v.replace(/\s+$/, "").length === idx + 1 ? "" : v.slice(idx + 1);
        // We can't easily preserve trailing whitespace; just replace at idx.
        node.nodeValue = v.slice(0, idx) + "\u00A0" + v.slice(idx + 1);
        // Suppress unused variable lint
        void trail;
        break;
      }
    }

    el.dataset.orphanFixed = "1";
  });
}
